import React, { useEffect, useRef } from 'react';
import { observer, inject } from 'mobx-react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@availity/element';

import CommonStore from '../../../Common/stores/CommonStore';
import MessageStore from '../../../Common/stores/MessageStore';

const highlightStyle = {
  default: { fill: '#2196f3', stroke: '#fff', strokeWidth: 2 },
  hover: { fill: '#2196f3', stroke: '#fff', strokeWidth: 2 },
  pressed: { fill: '#2196f3', stroke: '#fff', strokeWidth: 2 },
};

const defaultStyle = {
  default: { fill: '#fff', stroke: '#AAA', strokeWidth: 2 },
  hover: { fill: '#fff', stroke: '#AAA', strokeWidth: 2 },
  pressed: { fill: '#fff', stroke: '#AAA', strokeWidth: 2 },
};

const PublicFacingLandingPage = ({ commonStore, messageStore }) => {
  const {
    GEOJSON_URL,
    appStateBrandReportConfig,
    states = [],
    brandsByState = [],
    reportTypes = [],
    brand,
  } = commonStore;
  const { state, code, type } = brand;

  const { control } = useForm();
  const geoJsonRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    if (state && code && type) {
      (async () => {
        messageStore.disableMessage();
        commonStore.blocking = true;
        try {
          await commonStore.getTemplate();
          navigate('/wizard', { replace: true });
          commonStore.handleChangeCommonStore('publicImgHref', brand?.logo);
        } catch {
          const errorMessage = `System not available. Please try again.`;
          messageStore.setMessages(errorMessage, 'error');
          messageStore.populateMessages();
          window.scrollTo(0, 0);
        } finally {
          commonStore.blocking = false;
        }
      })();
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [state, code, type]);

  useEffect(() => {
    if (!state) return;
    if (brandsByState.length === 0) {
      const brandList = commonStore.filterBrandsBasedOnState(appStateBrandReportConfig, state);
      commonStore.handleChangeCommonStore('brandsByState', brandList);
    }
    if (!geoJsonRef.current) return;
    geoJsonRef?.current?.eachLayer((layer) => {
      const { name } = layer.feature.properties;
      layer.setStyle(name === state.value ? highlightStyle : defaultStyle);
    });
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [state]);

  const handleStateChange = (value) => {
    const stateObj = states.find((state) => state.code === value.code);
    const brandList = commonStore.filterBrandsBasedOnState(appStateBrandReportConfig, stateObj);
    commonStore.handleChangeCommonStore('brandsByState', brandList);
    commonStore.handleChangeCommonStore('reportTypes', []);
    commonStore.updateBrandDetails('state', stateObj);
    commonStore.updateBrandDetails('code', '');
    commonStore.updateBrandDetails('type', '');
    commonStore.updateBrandDetails('logoURL', '');
    if (stateObj && brandList.length === 1) {
      commonStore.extractBrandAndReportDeatils({ stateObj, brandList });
    }
  };

  const handleBrandClick = async (brandObj) => {
    const { code, logoURL } = brandObj;

    const reportsList = commonStore.filterReportTypesForStateAndBrand(appStateBrandReportConfig, state, code);
    commonStore.updateBrandDetails('code', code);
    commonStore.updateBrandDetails('logoURL', logoURL);
    commonStore.handleChangeCommonStore('reportTypes', reportsList);
    if (code && reportsList.length === 1) {
      commonStore.extractBrandAndReportDeatils({ code, reportsList });
    }
  };

  const handleReportTypeClick = (report) => {
    commonStore.updateBrandDetails('type', report.code);
  };

  return (
    <Box id="public-facing-wrapper" sx={{ margin: '5rem 0' }}>
      <Grid container spacing={3} id="map-container">
        <Grid size={{ xs: 12, md: 7 }} id="map-grid">
          <Controller
            name="selectedState"
            id="selectedState"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                {...field}
                filterSelectedOptions={false}
                value={state || null}
                options={states.length > 0 ? states?.sort((a, b) => a.value.localeCompare(b.value)) : []}
                disableClearable
                isOptionEqualToValue={(option, value) => option.code === value?.code}
                getOptionLabel={(option) => option.value}
                onChange={(event, value) => {
                  handleStateChange(value);
                }}
                onKeyDown={(event) => {
                  // Prevent form submit on Enter key of dropdown
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      <Typography variant="h5" id="state-dropdown-label" component="label" sx={{ mb: '1.25rem' }}>
                        Select a state
                      </Typography>
                    }
                    variant="outlined"
                    margin="normal"
                    placeholder="Select"
                  />
                )}
                sx={{ width: 300 }}
              />
            )}
          />
          {state && brandsByState.length > 1 && (
            <>
              <Typography variant="h5" id="brandsSpan" sx={{ mb: '1.25rem' }}>
                {state?.value ? `Select a brand for ${state?.value}` : 'Select a brand'}
              </Typography>
              <Grid container spacing={2} id="brand-container" sx={{ mb: '6.25rem' }}>
                {brandsByState.map((brand) => (
                  <Grid item xs={12} key={brand.code} id={`brand-item-${brand.code}`}>
                    <Card id={`card-${brand.code}`}>
                      <CardActionArea onClick={() => handleBrandClick(brand)} id={`card-action-${brand.code}`}>
                        <CardContent
                          style={{
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          <Box
                            component="img"
                            src={brand.logoURL}
                            alt="appLogo"
                            sx={{ height: 60, width: 200, ml: 2 }}
                            id={`logo-${brand.code}`}
                          />
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
          {reportTypes.length > 0 && (
            <>
              <Typography variant="h5" id="reportsSpan" sx={{ mb: '1.25rem' }}>
                Select the report to proceed
              </Typography>
              <Grid container spacing={2} id="report-container">
                <Stack direction="row" spacing={2}>
                  {reportTypes?.map((report) => (
                    <Button
                      key={report.code}
                      id={report.code}
                      variant="text"
                      color="primary"
                      sx={{ textTransform: 'none', fontSize: '1rem' }} // Increased font size
                      onClick={() => handleReportTypeClick(report)}
                    >
                      {report.value}
                    </Button>
                  ))}
                </Stack>
              </Grid>
            </>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 5 }} id="brand-selection-grid">
          <Grid container justifyContent="center" alignItems="center" id="brand-selection-grid-container">
            <Grid item size={{ xs: 12, md: 12 }} id="brand-selection-grid-child">
              <ComposableMap
                projection="geoAlbersUsa"
                width={1200}
                height={800}
                viewBox="200 100 800 800"
                id="composable-map"
              >
                <Geographies geography={GEOJSON_URL}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const { name } = geo.properties;
                      const style = name === state?.value ? highlightStyle : defaultStyle;
                      return <Geography key={geo.rsmKey} geography={geo} style={style} id={`geography-${name}`} />;
                    })
                  }
                </Geographies>
              </ComposableMap>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

PublicFacingLandingPage.propTypes = {
  commonStore: PropTypes.oneOf([CommonStore]),
  messageStore: PropTypes.oneOf([MessageStore]),
};

export default inject('commonStore', 'messageStore')(observer(PublicFacingLandingPage));
